import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { v, FieldRow, KeepTogether, SigningBlock, TemplateDocument, TemplateFooter, TemplateLetterhead, } from '../../../atoms/index.js';
import { expandPartySlot } from '../../../parties/index.js';
export const metadata = {
    // One-block notice — no numbered §§ (legacy layout). The Body renders a
    // letter-style heading + a single signing block.
    templateKey: 'strandningsmeddelande',
    version: 'v1',
    category: 'Bostadshyressättningsavtal',
    title: 'Strandningsmeddelande enligt 18 § hyresförhandlingslagen (1978:304)',
    shortTitle: 'Strandningsmeddelande',
    titleSuffix: { key: 'property_designation', prefix: '' },
    formNumber: 'Formulär R-21',
    preamble: 'Hyresvärdens meddelande om att förhandling enligt hyresförhandlingslagen har strandat (18 §).',
    description: 'Hyresvärdens meddelande till hyresgästorganisationen om att en förhandling enligt hyresförhandlingslagen (1978:304) har strandat — parterna har inte nått en överenskommelse och hyresvärden frånträder förhandlingarna enligt 18 §.\n\n' +
        'Vem undertecknar: hyresvärden. Motparten (hyresgästorganisationen) är mottagare.\n' +
        'Leverans: undertecknas och skickas med rekommenderad post eller överlämnas till förhandlingsmotparten (signeras ej via Scrive).\n\n' +
        'Använd efter en förhandlingsframställning där förhandlingen inte lett till överenskommelse — ange den ursprungliga framställningens datum.',
    status: 'active',
    signingMode: 'manual_delivery',
    signingPlacementAnchors: 'per-party-label',
    partySlots: [
        {
            id: 'lessor',
            label: 'Hyresvärd',
            role: 'lessor',
            ordinal: 0,
            required: true,
            signs: true,
        },
        {
            id: 'tenant_org',
            label: 'Hyresgästorganisation',
            role: 'other',
            ordinal: 0,
            required: true,
            signs: false,
            help: 'Den hyresgästorganisation som är part i förhandlingsordningen.',
        },
    ],
    // Party projection (ADR-0065) — only the mottagare (hyresgästorganisation)
    // is captured; the role name doubles as the field label.
    partyProjection: [
        {
            slot: 'tenant_org',
            section: 'Mottagare',
            fields: [
                {
                    attr: 'legal_name',
                    key: 'tenant_org_name',
                    label: 'Hyresgästorganisation',
                    requiredAtFinal: true,
                },
            ],
        },
    ],
    propertySlots: [
        {
            id: 'property',
            label: 'Fastighet',
            multi: false,
            required: true,
        },
    ],
};
export const variableSchema = [
    {
        key: 'request_date',
        label: 'Datum',
        type: 'date',
        section: 'Meddelande',
        requiredAtFinal: true,
        scopes: { copy: 'manual' },
    },
    // Mottagare — generated from metadata.partyProjection (ADR-0065).
    ...expandPartySlot(metadata, 'tenant_org'),
    {
        key: 'property_designation',
        label: 'Fastighet(er)',
        type: 'textarea',
        section: 'Fastighet',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'name' },
    },
    {
        key: 'property_address',
        label: 'Adress(er)',
        type: 'textarea',
        section: 'Fastighet',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'address' },
    },
    {
        key: 'framstallning_date',
        label: 'Förhandlingsframställning per den',
        type: 'date',
        section: 'Meddelande',
        requiredAtFinal: true,
        help: 'Datum för den förhandlingsframställning som förhandlingen avsåg.',
        scopes: { copy: 'manual' },
    },
];
export const sampleValues = {
    request_date: '2026-07-15',
    tenant_org_name: 'Hyresgästföreningen Region Stockholm',
    property_designation: 'Klockaren 5',
    property_address: 'Götgatan 35\n116 21 Stockholm',
    framstallning_date: '2026-06-02',
};
export function Letterhead() {
    return _jsx(TemplateLetterhead, { title: metadata.title, formNumber: metadata.formNumber });
}
export function PageFooter() {
    return _jsx(TemplateFooter, { formNumber: metadata.formNumber });
}
export function Body({ values, signatories, }) {
    return (_jsxs(_Fragment, { children: [_jsx("p", { className: "mb-1 text-center text-[13px] font-semibold uppercase tracking-wide", children: "Strandningsmeddelande" }), _jsx("p", { className: "mb-4 text-center text-[10.5px] italic text-stone-600", children: "enligt 18 \u00A7 hyresf\u00F6rhandlingslagen (1978:304)" }), _jsx(FieldRow, { label: "Datum", children: v(values.request_date) }), _jsx(FieldRow, { label: "Till", children: v(values.tenant_org_name) }), _jsx("h2", { className: "mt-5 mb-2 border-b border-stone-300 pb-1 text-[12px] font-bold", children: "Strandning av f\u00F6rhandling" }), _jsxs("div", { className: "text-[11px] leading-relaxed", children: [_jsx("p", { children: "Parterna har f\u00F6rhandlat enligt hyresf\u00F6rhandlingslagen betr\u00E4ffande:" }), _jsxs("div", { className: "my-2 border border-stone-300", children: [_jsx(FieldRow, { label: "Fastighet(er)", children: v(values.property_designation) }), _jsx(FieldRow, { label: "Adress(er)", children: v(values.property_address) })] }), _jsxs("p", { children: ["Avseende f\u00F6rhandlingsframst\u00E4llning per den", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.framstallning_date) }), "."] }), _jsx("p", { className: "mt-3", children: "Trots genomf\u00F6rda f\u00F6rhandlingar har parterna inte kunnat n\u00E5 en \u00F6verenskommelse. Hyresv\u00E4rden fr\u00E5ntr\u00E4der d\u00E4rf\u00F6r f\u00F6rhandlingarna enligt 18 \u00A7 hyresf\u00F6rhandlingslagen fr\u00E5n och med dagens datum." })] }), _jsx(KeepTogether, { children: _jsxs("div", { className: "mt-8", children: [_jsx("p", { className: "mb-2 text-[11px] font-semibold", children: "F\u00F6r hyresv\u00E4rden" }), _jsx(SigningBlock, { parties: [{ label: 'Hyresvärd', role: 'lessor', ordinal: 0 }], signatories: signatories })] }) })] }));
}
export function Component(props) {
    return (_jsx(TemplateDocument, { header: _jsx(Letterhead, {}), footer: _jsx(PageFooter, {}), children: _jsx(Body, { ...props }) }));
}
//# sourceMappingURL=index.js.map